import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges
} from '@angular/core'
import { DefaultValueAccessor, UntypedFormControl } from '@angular/forms'
import { audit, debounceTime, filter, map, shareReplay, startWith, tap } from 'rxjs/operators'
import { BehaviorSubject, combineLatest, merge, Scheduler, Subject, Subscription } from 'rxjs'
import { DeviceService } from '../../singleton-services/device.service'
import { IFacet } from '../models/facet.interface'
import { IAnalyticsProperties } from '../models/analytics-properties.interface'
import { PlatformService } from '../../singleton-services/platform.service'
import { RXJS_SCHEDULER } from '../../app.config'
import { WINDOW } from 'src/app/app.injections'

export interface IFacetSelection {
  facet: IFacet
  subfacet?: IFacet
}

@Component({
  selector: 'flo-vertical-search',
  styleUrls: ['./vertical-search.component.scss'],
  templateUrl: './vertical-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VerticalSearchComponent implements OnChanges, OnDestroy, OnInit, AfterViewInit {
  public get searchResultsCountString(): string {
    return `${this.resultsCount || 0} Videos`
  }

  @Input()
  set searchTerm(val: string) {
    this._searchTerm = val
    if (this.searchControl && val !== this.searchControl.value) {
      this.searchControl.setValue(val)
    }
  }
  get searchTerm() {
    return this._searchTerm
  }

  @Input()
  set debounceTime(val: number) {
    this._debounceTime = val
  }
  get debounceTime() {
    return this._debounceTime || 0
  }

  @Input()
  set searchName(val: string) {
    this._searchName = val
  }
  get searchName() {
    return this._searchName || ''
  }

  @Input()
  set totalSuffix(val: string) {
    this._totalSuffix = val
  }
  get totalSuffix() {
    return this._totalSuffix || 'Item'
  }

  @Input()
  set totalSuffixPlural(val: string) {
    this._totalSuffixPlural = val
  }
  get totalSuffixPlural() {
    return this._totalSuffixPlural || 'Items'
  }

  @Input()
  set total(val: number) {
    this._total = val
  }
  get total() {
    return this._total || 0
  }

  get showPluralSuffix() {
    return this.total > 1
  }

  constructor(
    @Inject(RXJS_SCHEDULER) private scheduler: Scheduler,
    private deviceService: DeviceService,
    @Inject(WINDOW) private readonly window: Window,
    private platformService: PlatformService
  ) {}
  @ContentChildren(DefaultValueAccessor)
  transcludedInputs: QueryList<DefaultValueAccessor>
  @Output()
  readonly searchChange = new EventEmitter<any>()
  @Output()
  readonly dynamicChange = new EventEmitter<any>()
  @Output()
  readonly queryChange = new EventEmitter<any>()
  @Input()
  public allFacets: ReadonlyArray<IFacet> = []
  @Input()
  public currentFacets: ReadonlyArray<IFacet> = []
  @Input()
  public selectedFacets: ReadonlyArray<IFacet> = []
  @Input()
  public resultsCount: number
  private subscriptions: ReadonlyArray<Subscription> = []

  transcludedOutputSource = new Subject<any>()
  transcludedOutput$ = this.transcludedOutputSource.asObservable()

  private _debounceTime: number
  private _total: number
  private _totalSuffix: string
  private _totalSuffixPlural: string
  private _searchName: string
  private _searchTerm: string
  public onSelectFacet = new Subject<IFacetSelection | undefined>()
  public onClearFilters = new Subject<ReadonlyArray<IFacet>>()
  public onApplyFilters = new Subject<ReadonlyArray<IFacet>>()
  public onCancelFilters = new Subject<ReadonlyArray<IFacet>>()
  public onExpandFacets = new Subject<boolean>()
  public onInputSelectedFacetsChange = new Subject<ReadonlyArray<IFacet>>()
  private selectedFacetsBS = new BehaviorSubject<ReadonlyArray<IFacet>>([])
  private clearedSelection$ = this.onClearFilters.pipe(map(() => []))

  private previousSelection: ReadonlyArray<IFacet> = this.selectedFacets || []
  private canceledSelection$ = this.onCancelFilters.pipe(map(() => this.previousSelection))

  private inputSelectedFacets$ = this.onInputSelectedFacetsChange.pipe(shareReplay(1))

  public updateSelectedFacets = merge(this.clearedSelection$, this.canceledSelection$, this.inputSelectedFacets$)
    .pipe(startWith(this.selectedFacets))
    .subscribe(selectedFacets => this.selectedFacetsBS.next(selectedFacets))

  public selectedFacets$ = this.selectedFacetsBS.pipe(shareReplay(1))

  public handleFacetSelection = this.onSelectFacet
    .pipe(
      filter(
        selection => !!selection && !!selection.subfacet && !!selection.subfacet.count && selection.subfacet.count > 0
      ),
      map(selection => this.collectSelectedFacets(selection))
    )
    .subscribe(selectedFacets => this.selectedFacetsBS.next(selectedFacets))

  public searchControl: UntypedFormControl

  private isMobile = true
  public updateDevice = this.deviceService.isMobile$.subscribe(isMobile => (this.isMobile = !!isMobile))

  public closeFacetsList = merge(this.onCancelFilters, this.onApplyFilters).subscribe(() =>
    this.onExpandFacets.next(false)
  )

  public toggleOverlappingElements = this.onExpandFacets
    .asObservable()
    .pipe(filter(() => this.isMobile && this.platformService.isBrowser))
    .subscribe(facetsAreExpanded => {
      const doc = this.window.document
      if (doc) {
        const navigation: HTMLElement | null = doc.querySelector('flo-primary-navigation')
        const adAdhesion: HTMLElement | null = doc.querySelector('flo-ad-adhesion')
        const adRectangle: HTMLElement | null = doc.querySelector('flo-ad-rectangle')
        if (navigation) navigation.hidden = facetsAreExpanded
        if (adAdhesion) adAdhesion.hidden = facetsAreExpanded
        if (adRectangle) adRectangle.hidden = facetsAreExpanded
      }
    })

  private collectSelectedFacets = (
    selection?: IFacetSelection,
    selectedFacets: ReadonlyArray<IFacet> = this.selectedFacetsBS.getValue()
  ): ReadonlyArray<IFacet> => {
    if (!selection) {
      return selectedFacets
    }
    const facetIndex = selectedFacets.findIndex(selectedFacet => selectedFacet.id === selection.facet.id)
    if (facetIndex < 0) {
      // new facet + subfacet =>facet added to selectedFacets
      if (selection.subfacet) {
        return selectedFacets.concat({
          id: selection.facet.id,
          value: selection.facet.value,
          count: selection.facet.count,
          displayName: selection.facet.displayName,
          subfacets: [
            {
              id: selection.subfacet.id,
              value: selection.subfacet.value,
              count: selection.subfacet.count,
              subfacets: selection.subfacet.subfacets
            }
          ]
        })
      } else {
        // new facet + no subfacet => return selected facets
        return selectedFacets
      }
    } else {
      if (selection.subfacet) {
        // existing facet + subfacet => is subfacet already selected?
        const subfacets = selectedFacets[facetIndex].subfacets || []
        if (!this.isFacetSelected(subfacets, selection.subfacet)) {
          selectedFacets[facetIndex].subfacets = subfacets.concat(selection.subfacet)
        } else {
          selectedFacets[facetIndex].subfacets = subfacets.filter(
            subfacet => subfacet.id !== (selection.subfacet as IFacet).id
          )
        }
        return selectedFacets.filter(facet => facet.subfacets && facet.subfacets.length)
      } else {
        // existing facet + no subfacet => facet is removed
        return selectedFacets.filter(selectedFacet => selectedFacet.id !== selection.facet.id)
      }
    }
  }

  public isFacetSelected = (selectedFacets: ReadonlyArray<IFacet>, facet: IFacet) =>
    selectedFacets.some(selectedFacet => selectedFacet.id === facet.id) ||
    selectedFacets.some(selectedFacet =>
      (selectedFacet.subfacets || []).some(selectedSubfacet => selectedSubfacet.id === facet.id)
    )

  public ngOnInit(): void {
    this.searchControl = new UntypedFormControl(this.searchTerm)
  }

  private applyAudit() {
    const applyOrMap = this.isMobile ? audit(() => this.onApplyFilters) : map(pass => pass)
    return applyOrMap
  }

  public ngAfterViewInit(): void {
    this.transcludedInputs.map(input => {
      const id = ((input as any)._elementRef.nativeElement as HTMLElement).id
      return input.registerOnChange(this.registerTranscluded.bind(this, id))
    })
    const searchControlSub = this.searchControl.valueChanges.subscribe(a => this.searchChange.next(a))
    this.subscriptions = this.subscriptions.concat(searchControlSub)

    const auditedFacets$ = combineLatest([this.selectedFacets$, this.deviceService.device$]).pipe(
      map(([selectedFacets, device]) => selectedFacets),
      this.applyAudit(),
      tap((facets: ReadonlyArray<IFacet>) => (this.previousSelection = facets)),
      startWith(this.selectedFacetsBS.getValue())
    )

    const querySub = combineLatest([
      this.searchChange.pipe(startWith(this.searchTerm)),
      auditedFacets$,
      this.transcludedOutput$.pipe(startWith({}))
    ])
      .pipe(
        map(([q, facets, dynamic]) => ({
          q,
          facets,
          ...dynamic
        })),
        debounceTime(this.debounceTime, this.scheduler),
        map(query => ({
          ...query,
          q: query.q === '' ? undefined : query.q,
          facets: mapFacetsToQueryObject(query.facets)
        }))
      )
      .subscribe(query => this.queryChange.next(query))
    this.subscriptions = this.subscriptions.concat(querySub)
  }

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges.currentFacets && simpleChanges.currentFacets.currentValue) {
      const currentFacets: ReadonlyArray<IFacet> = simpleChanges.currentFacets.currentValue
      this.currentFacets = (this.allFacets || [])
        .map(facet => updateFacetCounts(facet, currentFacets))
        .filter(facet => facet.subfacets && facet.subfacets.length)
    } else if ((this.allFacets && !this.currentFacets) || !this.currentFacets.length) {
      this.currentFacets = this.allFacets.filter(facet => facet && facet.subfacets && facet.subfacets.length)
    }
    if (simpleChanges.selectedFacets && simpleChanges.selectedFacets.currentValue) {
      const selectedFacets = simpleChanges.selectedFacets.currentValue
      this.previousSelection = selectedFacets
      this.onInputSelectedFacetsChange.next(selectedFacets)
    }
  }

  private registerTranscluded(id: string, value: string): void {
    this.transcludedOutputSource.next({ [id]: value })
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe())
  }

  public getFacetAnalyticsProperties = (facet: IFacet, subfacet: IFacet): IAnalyticsProperties => ({
    filter: subfacet.value,
    filter_parent: facet.value
  })

  public getMoreAnalyticsProperties = (facet: IFacet): IAnalyticsProperties => ({
    name: 'More - Training Facets',
    filter: facet.value
  })

  public getApplyAnalyticsProperties = (selectedFacets: ReadonlyArray<IFacet>): IAnalyticsProperties => ({
    name: selectedFacets
      .map((facet: any) => `${facet.value}: ${facet.subfacets.map((subfacet: IFacet) => subfacet.value).join(',')}`)
      .join(',')
  })

  public getFiltersAnalyticsProperties = (): IAnalyticsProperties => ({
    name: 'Filters Expanded'
  })

  public trackByFacet(facet: IFacet): string {
    return facet.id
  }
}

function mapFacetsToQueryObject(facets: ReadonlyArray<IFacet>) {
  if (!facets.length) return undefined
  return facets.reduce((queryObject: { [key: string]: string | ReadonlyArray<string> }, facet) => {
    if (!facet.subfacets) {
      return queryObject
    } else if (facet.subfacets.length === 1) {
      queryObject[facet.value] = facet.subfacets[0].value
    } else {
      queryObject[facet.value] = facet.subfacets.map(subfacet => subfacet.value)
    }
    return queryObject
  }, {})
}

function updateFacetCounts(facet: IFacet, currentFacets: ReadonlyArray<IFacet>): IFacet {
  const currentFacet: IFacet = currentFacets.find(arrayFacet => arrayFacet.id === facet.id) || {
    count: 0,
    subfacets: [],
    id: '',
    value: ''
  }
  return {
    ...facet,
    count: currentFacet.count,
    subfacets: (facet.subfacets || []).map(subfacet => ({
      ...subfacet,
      count:
        (
          (currentFacet.subfacets || []).find((currentSubfacet: IFacet) => currentSubfacet.id === subfacet.id) || {
            count: 0
          }
        ).count || 0
    }))
  }
}
